import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { ReactNode, useEffect, lazy } from 'react';
import { Button, Result, Spin } from 'antd';
import { RequireAuth } from '../components/RequireAuth';
import RouteError from '../components/RouteError';
import { useTenantAuth } from '../stores/auth.tenant';
import { tenantApi } from '../api/tenant';
import TenantLoginPage from '../pages/login/TenantLoginPage';
import DelegatedEntryPage from '../pages/login/DelegatedEntryPage';
import TenantLayout from '../layouts/TenantLayout';
import { useGeoContext } from '../hooks/useGeoContext';
import { TenantSessionChangedError, readStoredTenantJwt } from '../utils/tenant-session';
const DashboardPage = lazy(() => import('../pages/tenant/DashboardPage'));
const H3yunTrainingDashboardPage = lazy(() => import('../pages/tenant/H3yunTrainingDashboardPage'));
const ChangePasswordPage = lazy(() => import('../pages/tenant/ChangePasswordPage'));
const ProfilePage = lazy(() => import('../pages/tenant/ProfilePage'));
const EmployeeListPage = lazy(() => import('../pages/tenant/EmployeeListPage'));
const OrgStructurePage = lazy(() => import('../pages/tenant/OrgStructurePage'));
const EmployeeDetailPage = lazy(() => import('../pages/tenant/EmployeeDetailPage'));
const CardReviewPage = lazy(() => import('../pages/tenant/CardReviewPage'));
const InvoiceIssuePage = lazy(() => import('../pages/tenant/InvoiceIssuePage'));
const CompanyBriefEditPage = lazy(() => import('../pages/tenant/CompanyBriefEditPage'));
const HighlightsPage = lazy(() => import('../pages/tenant/HighlightsPage'));
const MiniappStylePage = lazy(() => import('../pages/tenant/MiniappStylePage'));
const WorkbenchTabsPage = lazy(() => import('../pages/tenant/WorkbenchTabsPage'));
const HighlightEditPage = lazy(() => import('../pages/tenant/HighlightEditPage'));
const SubscriptionPage = lazy(() => import('../pages/tenant/SubscriptionPage'));
const OrderListPage = lazy(() => import('../pages/tenant/OrderListPage'));
const InvoiceRequestPage = lazy(() => import('../pages/tenant/InvoiceRequestPage'));
const MessageBoardPage = lazy(() => import('../pages/tenant/MessageBoardPage'));
const FormSchemaPage = lazy(() => import('../pages/tenant/FormSchemaPage'));
const AnnouncementsPage = lazy(() => import('../pages/tenant/AnnouncementsPage'));
const AnnouncementEditPage = lazy(() => import('../pages/tenant/AnnouncementEditPage'));
const LoginLogsPage = lazy(() => import('../pages/tenant/LoginLogsPage'));
const GroupHomePage = lazy(() => import('../pages/tenant/group/GroupHomePage'));
const SubsidiariesPage = lazy(() => import('../pages/tenant/group/SubsidiariesPage'));
const SeatGrantsPage = lazy(() => import('../pages/tenant/group/SeatGrantsPage'));
const RelationApplicationsPage = lazy(() => import('../pages/tenant/group/RelationApplicationsPage'));
const ApplyToGroupPage = lazy(() => import('../pages/tenant/group/ApplyToGroupPage'));
const GroupBindingPage = lazy(() => import('../pages/tenant/group/GroupBindingPage'));
const PendingReviewPage = lazy(() => import('../pages/tenant/PendingReviewPage'));
const ExportsPage = lazy(() => import('../pages/tenant/ExportsPage'));
const RoleListPage = lazy(() => import('../pages/tenant/RoleListPage'));
const RoleEditPage = lazy(() => import('../pages/tenant/RoleEditPage'));
const SeatRosterPage = lazy(() => import('../pages/tenant/SeatRosterPage'));
const AiUsagePage = lazy(() => import('../pages/tenant/AiUsagePage'));
const CoinPoolPage = lazy(() => import('../pages/tenant/CoinPoolPage')); // ADR-49
const CustomerServicePage = lazy(() => import('../pages/tenant/CustomerServicePage'));
const RagKnowledgePage = lazy(() => import('../pages/tenant/RagKnowledgePage'));
const EnterpriseShopPage = lazy(() => import('../pages/tenant/EnterpriseShopPage'));
const GeoDashboardPage = lazy(() => import('../pages/tenant/geo/DashboardPage'));
const GeoContentListPage = lazy(() => import('../pages/tenant/geo/ContentListPage'));
const GeoContentEditorPage = lazy(() => import('../pages/tenant/geo/ContentEditorPage'));
const GeoChannelPublishPage = lazy(() => import('../pages/tenant/geo/ChannelPublishPage'));
const GeoPublishWorkbenchPage = lazy(() => import('../pages/tenant/geo/PublishWorkbenchPage'));

function GeoRoute({ children, allowGroup = false }: { children: ReactNode; allowGroup?: boolean }) {
  const tenant = useTenantAuth(s => s.tenant);
  const context = useGeoContext();
  if (!tenant) return <Spin />;
  if (allowGroup && tenant.tenant_type === 'group' && tenant.is_group_root) return <>{children}</>;
  if (tenant.tenant_type !== 'enterprise' && tenant.tenant_type !== 'government') return <Result status="403" title="无权限访问" />;
  if (context.isLoading) return <Spin />;
  if (context.isError) return <Result status="500" title="GEO状态读取失败" extra={<Button onClick={() => void context.refetch()}>重试</Button>} />;
  if (!context.data?.opened) return <Result status="info" title="尚未开通 GEO" subTitle="请联系平台管理员开通后再使用。" />;
  return <>{children}</>;
}

function GeoEnterpriseRoute({ children }: { children: ReactNode }) {
  return <GeoRoute>{children}</GeoRoute>;
}

function GeoOverviewRoute() {
  return <GeoRoute allowGroup><GeoDashboardPage /></GeoRoute>;
}

function Auth({ children }: { children: ReactNode }) {
  const jwt = useTenantAuth(s => s.jwt);
  const user = useTenantAuth(s => s.user);
  const setUser = useTenantAuth(s => s.setUser);
  const logout = useTenantAuth(s => s.logout);
  const mustChange = useTenantAuth(s => s.must_change_password);
  const location = useLocation();

  // store 仅持久化 jwt；刷新后 user 丢失 → 用 jwt 调 /me 重新水合，
  // 否则 useIsOwner 因 user=null 恒返回 false，创始人专属操作（公司简介/席位/集团等）被错误禁用。
  useEffect(() => {
    if (!jwt || user) return;
    tenantApi.getMe()
      .then(me => useTenantAuth.getState().commitIfCurrent(jwt, () => setUser(me)))
      .catch(error => {
        if (error instanceof TenantSessionChangedError) return;
        const state = useTenantAuth.getState();
        if (state.jwt === jwt && readStoredTenantJwt() === jwt) state.logoutIfCurrent(jwt);
      });
  }, [jwt, user, setUser, logout]);

  // 有 jwt 但 user 还没水合完 → 等一下，避免 owner 被瞬间误判
  if (jwt && !user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (jwt && mustChange && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <RequireAuth jwt={jwt} loginPath="/login">{children}</RequireAuth>;
}

export const tenantRouter = createBrowserRouter([
  {
    // pathless 根路由：统一 errorElement 兜底所有子路由抛出的渲染错误 /
    // chunk 加载失败，取代 React Router 默认的 "Unexpected Application Error!"。
    errorElement: <RouteError />,
    children: [
  { path: '/login', element: <TenantLoginPage /> },
  { path: '/delegated-entry', element: <DelegatedEntryPage /> },
  // /activate 已下线：后端从未实现 auth/activate；员工加入走花名册邀请 + 小程序同意（ADR-34）
  { path: '/change-password', element: <Auth><ChangePasswordPage /></Auth> },
  {
    path: '/',
    element: <Auth><TenantLayout /></Auth>,
    children: [
      { index: true, element: <Navigate to="/dashboard" /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'h3yun-training', element: <H3yunTrainingDashboardPage /> },
      { path: 'geo-content/overview', element: <GeoOverviewRoute /> },
      { path: 'geo-content/analysis', element: <GeoEnterpriseRoute><GeoContentListPage /></GeoEnterpriseRoute> },
      { path: 'geo-content/analysis/:contentId', element: <GeoEnterpriseRoute><GeoContentEditorPage /></GeoEnterpriseRoute> },
      { path: 'geo-content/publish', element: <GeoEnterpriseRoute><GeoChannelPublishPage /></GeoEnterpriseRoute> },
      { path: 'geo-content/publish/:contentId', element: <GeoEnterpriseRoute><GeoPublishWorkbenchPage /></GeoEnterpriseRoute> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'company-brief', element: <CompanyBriefEditPage /> },
      // official-videos 独立页已删（V5）：官方视频并入 /company-brief 简介页管理
      // 部门口径方案 A：Department 树页下线，部门统一花名册「部门」文本
      { path: 'announcements', element: <AnnouncementsPage /> },
      { path: 'announcements/new', element: <AnnouncementEditPage /> },
      { path: 'announcements/:id/edit', element: <AnnouncementEditPage /> },
      { path: 'employees', element: <EmployeeListPage /> },
      { path: 'employees/:id', element: <EmployeeDetailPage /> },
      { path: 'org-structure', element: <OrgStructurePage /> }, // ADR-46 组织架构复建
      { path: 'cards/pending-review', element: <CardReviewPage /> },
      // V5 内容体系双域：/highlights=企业亮点（图/视频）、/news=企业资讯（图文 only），
      // 同一组件按 category 参数化（B 端同一张表按 category 分域）
      { path: 'highlights', element: <HighlightsPage category="highlight" /> },
      { path: 'miniapp-style', element: <MiniappStylePage /> }, // 小程序风格
      { path: 'workbench-tabs', element: <WorkbenchTabsPage /> }, // 工作台 Tab 配置
      { path: 'highlights/new', element: <HighlightEditPage category="highlight" /> },
      { path: 'highlights/:id/edit', element: <HighlightEditPage category="highlight" /> },
      { path: 'news', element: <HighlightsPage category="news" /> },
      { path: 'news/new', element: <HighlightEditPage category="news" /> },
      { path: 'news/:id/edit', element: <HighlightEditPage category="news" /> },
      { path: 'subscription', element: <SubscriptionPage /> },
      { path: 'orders', element: <OrderListPage /> },
      { path: 'invoices', element: <InvoiceRequestPage /> },
      { path: 'invoices/issue', element: <InvoiceIssuePage /> },
      { path: 'message-board', element: <MessageBoardPage /> },
      // 旧路径重定向到合并后的留言板
      { path: 'leads', element: <Navigate to="/message-board" replace /> },
      { path: 'form-schema', element: <FormSchemaPage /> },
      { path: 'login-logs', element: <LoginLogsPage /> },
      { path: 'ai-usage', element: <AiUsagePage /> },
      { path: 'coin-pool', element: <CoinPoolPage /> },
      { path: 'customer-service', element: <CustomerServicePage /> },
      { path: 'rag', element: <RagKnowledgePage /> },
      { path: 'enterprise-shop', element: <EnterpriseShopPage /> },
      { path: 'group/home', element: <GroupHomePage /> },
      { path: 'group/subsidiaries', element: <SubsidiariesPage /> },
      { path: 'group/seats', element: <SeatGrantsPage /> },
      { path: 'group/applications', element: <RelationApplicationsPage /> },
      { path: 'group/apply', element: <ApplyToGroupPage /> },
      { path: 'group/binding', element: <GroupBindingPage /> },
      { path: 'seat-roster', element: <SeatRosterPage /> },
      { path: 'profiles/pending-review', element: <PendingReviewPage /> },
      { path: 'gov-messages', element: <Navigate to="/message-board" replace /> },
      { path: 'exports', element: <ExportsPage /> },
      { path: 'roles', element: <RoleListPage /> },
      { path: 'roles/:id/edit', element: <RoleEditPage /> },
    ],
  },
  { path: '*', element: <div style={{padding: 40}}>404 Not Found</div> },
    ],
  },
]);
